import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import { logSistemaService, ConfigLog, ConfigLogResponse, NivelLog } from '../../services/logSistemaService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSalvo: () => void;
}

const NIVEIS: { valor: NivelLog; label: string }[] = [
  { valor: 'ERROR', label: 'Só erros' },
  { valor: 'WARN', label: 'Erros e avisos' },
  { valor: 'INFO', label: 'Erros, avisos e informações (recomendado)' },
  { valor: 'DEBUG', label: 'Tudo, incluindo debug' }
];

const campoSelect = 'h-9 w-full text-sm rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-100 px-2';

const Linha: React.FC<{ titulo: string; descricao: string; children: React.ReactNode }> = ({ titulo, descricao, children }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <div className="min-w-0">
      <Label className="text-sm">{titulo}</Label>
      <p className="text-xs text-gray-500 dark:text-gray-400">{descricao}</p>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const ConfigLogsDialog: React.FC<Props> = ({ open, onOpenChange, onSalvo }) => {
  const [info, setInfo] = useState<ConfigLogResponse | null>(null);
  const [form, setForm] = useState<ConfigLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    logSistemaService.getConfig()
      .then((r) => { setInfo(r); setForm(r.config); })
      .catch(() => toast({ title: 'Não foi possível carregar as configurações.', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [open]);

  const set = <K extends keyof ConfigLog>(chave: K, valor: ConfigLog[K]) =>
    setForm((f) => (f ? { ...f, [chave]: valor } : f));

  const numero = (chave: 'retencaoDias' | 'limiteDiarioMB' | 'requisicaoLentaMs', texto: string) =>
    set(chave, (texto === '' ? 0 : Number(texto)) as ConfigLog[typeof chave]);

  const salvar = async () => {
    if (!form) return;
    setSalvando(true);
    try {
      await logSistemaService.saveConfig(form);
      toast({ title: 'Configurações de log salvas. Os outros ambientes aplicam em até 15 segundos.' });
      onSalvo();
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Não foi possível salvar as configurações.', variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const lim = info?.limites;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurações dos logs</DialogTitle>
          <DialogDescription>
            Valem para todos os ambientes (PRD, HML, PET…). Cada ambiente aplica a mudança em até 15 segundos, sem reiniciar.
          </DialogDescription>
        </DialogHeader>

        {loading || !form || !info || !lim ? (
          <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            <Linha titulo="Dias guardados" descricao={`Arquivos mais antigos são apagados sozinhos (${lim.retencaoDias.min} a ${lim.retencaoDias.max}). Hoje conta como 1.`}>
              <Input type="number" className="w-24 h-9" min={lim.retencaoDias.min} max={lim.retencaoDias.max}
                value={form.retencaoDias || ''} onChange={(e) => numero('retencaoDias', e.target.value)} />
            </Linha>

            <Linha titulo="Limite por dia (MB)" descricao={`Máximo gravado por ambiente em cada dia (${lim.limiteDiarioMB.min} a ${lim.limiteDiarioMB.max}). Protege o disco da VPS.`}>
              <Input type="number" className="w-24 h-9" min={lim.limiteDiarioMB.min} max={lim.limiteDiarioMB.max}
                value={form.limiteDiarioMB || ''} onChange={(e) => numero('limiteDiarioMB', e.target.value)} />
            </Linha>

            <Linha titulo="Nível mínimo" descricao="O que for menos importante que isso não é gravado.">
              <select className={`${campoSelect} w-56`} value={form.nivelMinimo} onChange={(e) => set('nivelMinimo', e.target.value as NivelLog)}>
                {NIVEIS.map((n) => <option key={n.valor} value={n.valor}>{n.label}</option>)}
              </select>
            </Linha>

            <Linha titulo="Gravar requisições da API" descricao="Registra chamadas /api que alteram dados (POST, PUT, DELETE), que dão erro ou que são lentas.">
              <Switch checked={form.registrarHttp} onCheckedChange={(v) => set('registrarHttp', v)} />
            </Linha>

            {form.registrarHttp && (
              <>
                <Linha titulo="Considerar lenta a partir de (ms)" descricao={`Leituras (GET) que demorarem mais que isso são gravadas (${lim.requisicaoLentaMs.min} a ${lim.requisicaoLentaMs.max}).`}>
                  <Input type="number" className="w-24 h-9" min={lim.requisicaoLentaMs.min} max={lim.requisicaoLentaMs.max}
                    value={form.requisicaoLentaMs || ''} onChange={(e) => numero('requisicaoLentaMs', e.target.value)} />
                </Linha>
                <Linha titulo="Gravar também todas as leituras (GET)" descricao="Aumenta muito o volume. Use só para investigar algo e depois desligue.">
                  <Switch checked={form.incluirLeituras} onCheckedChange={(v) => set('incluirLeituras', v)} />
                </Linha>
              </>
            )}

            <Linha titulo="Gravar webhooks" descricao="Evolution, Uazapi e Meta: rota, evento, status e tempo. Sem o corpo da mensagem.">
              <Switch checked={form.registrarWebhooks} onCheckedChange={(v) => set('registrarWebhooks', v)} />
            </Linha>

            <Linha titulo="Gravar a saída do console" descricao="Tudo que o código já escreve com console.log/warn/error (o que aparece no docker logs).">
              <Switch checked={form.capturarConsole} onCheckedChange={(v) => set('capturarConsole', v)} />
            </Linha>

            <div className="py-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <p>
                <span className="font-medium">Pasta dos logs:</span>{' '}
                <code className="px-1 rounded bg-gray-100 dark:bg-slate-800">{info.pastaLogs}</code> (dentro do container do ambiente «{info.ambienteAtual}»).
              </p>
              <p>
                A pasta não é configurada por aqui: ela é um volume do Docker, definido por <code className="px-1 rounded bg-gray-100 dark:bg-slate-800">LOG_HOST_DIR</code> no <code className="px-1 rounded bg-gray-100 dark:bg-slate-800">.env</code> do servidor.
              </p>
              {!info.gravacaoAtiva && (
                <p className="text-red-500">A gravação em arquivo está desativada neste ambiente (não foi possível criar a pasta).</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            disabled={!info || salvando}
            onClick={() => info && setForm(info.padrao)}
          >
            Restaurar padrão
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={!form || salvando} className="gap-1.5">
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigLogsDialog;
